<template>
    <div class="basket-items">
        <!-- Empty State -->
        <div v-if="items.length === 0" class="text-center py-12">
            <svg class="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/>
            </svg>
            <h3 class="mt-4 text-lg font-medium text-gray-900">Your basket is empty</h3>
            <p class="mt-2 text-gray-500">Start shopping to add items to your basket.</p>
            <a href="/" class="mt-6 inline-block bg-brill-blue text-white px-6 py-3 rounded-lg font-medium hover:bg-brill-blue-dark">
                Continue Shopping
            </a>
        </div>

        <!-- Items List -->
        <div v-else>
            <div
                v-for="item in items"
                :key="item.sku"
                class="flex items-start py-6 border-b border-gray-200"
            >
                <!-- Image -->
                <a :href="item.url" class="flex-shrink-0">
                    <img
                        :src="item.image"
                        :alt="item.title"
                        class="w-24 h-24 object-cover rounded-lg"
                    />
                </a>

                <!-- Details -->
                <div class="ml-4 flex-1">
                    <a :href="item.url" class="text-gray-900 font-medium hover:text-brill-blue">
                        {{ item.title }}
                    </a>
                    <p v-if="item.condition" class="text-sm text-gray-500 mt-1">{{ item.condition }}</p>

                    <!-- Price & Quantity -->
                    <div class="mt-3 flex items-center justify-between">
                        <div class="flex items-center">
                            <!-- Quantity Controls -->
                            <div class="flex items-center border border-gray-300 rounded">
                                <button
                                    @click="updateQuantity(item.sku, item.quantity - 1)"
                                    :disabled="item.quantity <= 1 || updating === item.sku"
                                    class="px-2 py-1 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                                >
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4"/>
                                    </svg>
                                </button>
                                <span class="px-3 py-1 text-center min-w-[2.5rem]">
                                    <span v-if="updating === item.sku" class="animate-pulse">...</span>
                                    <span v-else>{{ item.quantity }}</span>
                                </span>
                                <button
                                    @click="updateQuantity(item.sku, item.quantity + 1)"
                                    :disabled="item.quantity >= item.maxQuantity || updating === item.sku"
                                    class="px-2 py-1 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                                >
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                                    </svg>
                                </button>
                            </div>

                            <!-- Remove Button -->
                            <button
                                @click="removeItem(item.sku)"
                                :disabled="updating === item.sku"
                                class="ml-4 text-sm text-red-600 hover:text-red-800"
                            >
                                Remove
                            </button>
                        </div>

                        <!-- Price -->
                        <div class="text-right">
                            <p class="text-lg font-bold text-gray-900">£{{ formatPrice(item.price * item.quantity) }}</p>
                            <p v-if="item.quantity > 1" class="text-sm text-gray-500">£{{ formatPrice(item.price) }} each</p>
                            <p class="text-sm text-gray-500">{{ item.postageDisplay }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Summary -->
            <div class="mt-8 bg-gray-50 rounded-lg p-6">
                <div class="flex justify-between text-base text-gray-900 mb-2">
                    <p>Subtotal</p>
                    <p>£{{ formatPrice(subtotal) }}</p>
                </div>
                <div class="flex justify-between text-base text-gray-900 mb-4">
                    <p>Postage</p>
                    <p>{{ totalPostage > 0 ? '£' + formatPrice(totalPostage) : 'Free' }}</p>
                </div>
                <div class="border-t border-gray-200 pt-4 flex justify-between text-lg font-bold text-gray-900">
                    <p>Total</p>
                    <p>£{{ formatPrice(total) }}</p>
                </div>

                <a
                    href="/checkout"
                    class="mt-6 block w-full bg-brill-red text-white text-center py-3 px-6 rounded-lg font-semibold hover:bg-brill-red-dark transition-colors"
                >
                    Proceed to Checkout
                </a>

                <a href="/" class="mt-3 block text-center text-brill-blue hover:underline">
                    Continue Shopping
                </a>
            </div>
        </div>

        <!-- Error Message -->
        <div v-if="error" class="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {{ error }}
        </div>
    </div>
</template>

<script>
export default {
    name: 'BasketItems',

    props: {
        initialItems: {
            type: Array,
            default: () => [],
        },
    },

    data() {
        return {
            items: this.initialItems,
            updating: null,
            error: null,
        };
    },

    computed: {
        subtotal() {
            return this.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
        },
        totalPostage() {
            return this.items.reduce((sum, item) => sum + (item.postage || 0), 0);
        },
        total() {
            return this.subtotal + this.totalPostage;
        },
    },

    methods: {
        formatPrice(value) {
            return parseFloat(value).toFixed(2);
        },

        async updateQuantity(sku, newQuantity) {
            if (newQuantity < 1) {
                return this.removeItem(sku);
            }

            this.updating = sku;
            this.error = null;

            try {
                const response = await fetch('/basket/update', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
                    },
                    body: JSON.stringify({ sku, quantity: newQuantity }),
                });

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.message || 'Failed to update quantity');
                }

                // Update local state
                const item = this.items.find(i => i.sku === sku);
                if (item) {
                    item.quantity = newQuantity;
                }

                // Update header count
                this.updateHeaderCount(data.basketCount);

            } catch (err) {
                this.error = err.message;
            } finally {
                this.updating = null;
            }
        },

        async removeItem(sku) {
            this.updating = sku;
            this.error = null;

            try {
                const response = await fetch('/basket/remove', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content,
                    },
                    body: JSON.stringify({ sku }),
                });

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.message || 'Failed to remove item');
                }

                // Remove from local state
                this.items = this.items.filter(i => i.sku !== sku);

                // Update header count
                this.updateHeaderCount(data.basketCount);

            } catch (err) {
                this.error = err.message;
            } finally {
                this.updating = null;
            }
        },

        updateHeaderCount(count) {
            window.dispatchEvent(new CustomEvent('basket-updated', {
                detail: { count }
            }));
        },
    },
};
</script>

<style scoped>
.bg-brill-red {
    background-color: #C41E3A;
}
.bg-brill-red-dark {
    background-color: #A01830;
}
.hover\:bg-brill-red-dark:hover {
    background-color: #A01830;
}
.bg-brill-blue {
    background-color: #1E4D8C;
}
.hover\:bg-brill-blue-dark:hover {
    background-color: #163A6B;
}
.text-brill-blue {
    color: #1E4D8C;
}
.hover\:text-brill-blue:hover {
    color: #1E4D8C;
}
</style>
